// ───────────────────────────────────────────────────────────────────────────
// Demo core: fake JWT
// A structurally valid but unsigned JWT ("header.payload.demo") so every piece
// of the app that decodes the access token (jwt-decode, normalizeTokenPayload,
// canAccess, the org switcher) keeps working with no backend.
//
// Flat wildcard permissions: 'recyclee.<org>.*' satisfies every
// 'recyclee.<org>.<site>.<entity>.<action>' check, and both org ids are parsed
// out of these strings so both demo orgs show up in the org switcher.
// Claims are ASCII-only on purpose: btoa cannot encode non-Latin1 characters.
// ───────────────────────────────────────────────────────────────────────────

import { DEMO_OUTLET_ORG_ID, DEMO_RECYCLER_ORG_ID, DEMO_USER_ID, DEMO_USERNAME } from './constants';

export interface FakeJwtPayload {
  sub: string;
  username: string;
  typ: 'access';
  iat: number;
  exp: number;
  permissions: string[];
  platform_permissions: string[];
  sites: Record<string, string[]>;
  is_pilot: false;
}

/** Signature segment of every demo token; lets code recognise a demo token. */
export const FAKE_JWT_SIGNATURE = 'demo';

const ONE_YEAR_S = 60 * 60 * 24 * 365;

function base64UrlEncode(value: unknown): string {
  return btoa(JSON.stringify(value)).replace(/=+$/, '').replace(/\+/g, '-').replace(/\//g, '_');
}

function base64UrlDecode(segment: string): string {
  const b64 = segment.replace(/-/g, '+').replace(/_/g, '/');
  const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
  return atob(padded);
}

export function buildFakeJwtPayload(nowMs: number = Date.now()): FakeJwtPayload {
  const iat = Math.floor(nowMs / 1000);
  return {
    sub: DEMO_USER_ID,
    username: DEMO_USERNAME,
    typ: 'access',
    iat,
    exp: iat + ONE_YEAR_S,
    permissions: [`recyclee.${DEMO_OUTLET_ORG_ID}.*`, `recyclee.${DEMO_RECYCLER_ORG_ID}.*`],
    platform_permissions: [],
    sites: { [DEMO_OUTLET_ORG_ID]: ['*'], [DEMO_RECYCLER_ORG_ID]: ['*'] },
    is_pilot: false,
  };
}

/** Unsigned demo access token, valid (exp) for one year from now. */
export function buildFakeJwt(): string {
  const header = { alg: 'none', typ: 'JWT' };
  return `${base64UrlEncode(header)}.${base64UrlEncode(buildFakeJwtPayload())}.${FAKE_JWT_SIGNATURE}`;
}

/** Decode the payload of a demo token (or any JWT). Throws on malformed input. */
export function decodeFakeJwtPayload(token: string): FakeJwtPayload {
  const parts = token.split('.');
  if (parts.length !== 3) throw new Error('[demo] malformed JWT');
  return JSON.parse(base64UrlDecode(parts[1])) as FakeJwtPayload;
}

/** True when `token` looks like a token produced by buildFakeJwt(). */
export function isFakeJwt(token: string | null | undefined): boolean {
  if (!token) return false;
  const parts = token.split('.');
  return parts.length === 3 && parts[2] === FAKE_JWT_SIGNATURE;
}
